/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { QueryClient } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { useRouter } from 'next/router'
import NotFound from 'errors/not-found'
import InternalError from 'errors/internal-error'
import Footer from 'common/footer'
import OpenGraph from 'common/open-graph'
import PricingSection from 'course/pricing/pricing-section'
import StaticWarranty from 'course/pricing/static-warranty'
import CourseStatistics from 'course/course-statistics'
import CourseTeacherMessage from 'course/course-teacher-message'
import CourseRequirements from 'course/course-requirements'
import LandingNavigation from 'course/landing-navigation'
import FreeClass from 'course/free-class'
import ReviewListRedesing from 'components/course/review/review-list-redesing'
import Faq from 'components/course/faq'
import ComunityReviews from 'common/community-reviews'
import Navigation from 'components/navigation'
import { getCourseListService, getCourseService } from 'services/course'
import { useCourse, prefetchCourse, prefetchBadge } from 'querys/course'
import { prefetchReviews } from 'querys/reviews'
import ProgresiveLearning from 'course/progressive-learning/progressive-learning'
import Lessons from 'course/lessons'
import ProjectCourse from 'course/project'
import BadgeExposition from 'course/badge-exposition'
import VideoCardRedesing from 'course/video-card'
// import MobileBanner from 'course/mobile-banner'
import { prefetchCourseLessons } from 'querys/lesson'
import { prefetchSubscriptionPlans } from 'querys/subscriptions'
// import Warranty from 'course/pricing/warranty'
import LoadingState from 'common/loading-state'
import flatEditorJsContent from 'utils/flat-editor-js-content'

const NewContainer = styled.main`
  .details {
    margin: 0 auto;
    padding: 0 1rem;
  }
  @media screen and (min-width: 768px) {
    .details {
      padding: 0 3rem;
    }
  }
  @media screen and (min-width: 1024px) {
    padding: 0 3rem;
    margin: auto;
    max-width: 1200px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-areas: 'details videoCard';
    column-gap: 4em;
    .details {
      padding: 0;
      grid-area: details;
    }
  }
  @media screen and (min-width: 1366px) {
    grid-template-columns: 1fr 450px;
  }
  @media screen and (min-width: 1366px) {
    padding: 0 5rem;
  }
`

function Course({ course_slug, openGraph }) {
  const router = useRouter()
  const { data, isLoading, error } = useCourse()
  const [reviewCount, setReviewCount] = useState(1)
  const [active, setActive] = useState('descripcion')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('utm_source') === 'fbAds') {
      sessionStorage.setItem('fbAds', 'true')
    }
  }, [])

  if (isLoading || !data) return <LoadingState />
  if (router.isFallback) return <NotFound />

  if (error?.message === 'User Unauthorized') return null
  if (error?.message === 'Not Found') return <NotFound />
  if (error?.message === 'Internal Error') return <InternalError />

  const {
    title, description, url, image, keyword,
  } = openGraph

  return (
    <>
      <OpenGraph
        title={title}
        description={description}
        url={url}
        image={image}
        keyword={keyword}
      />
      <Navigation position="fixed" noCountdown background="var(--sky)" />
      <NewContainer id="descripcion">
        <VideoCardRedesing />
        <div className="details" id="course">
          <LandingNavigation active={active} setActive={setActive} />
          <CourseRequirements />
          <CourseTeacherMessage setActive={setActive} />
          <CourseStatistics />
          <FreeClass />
          <BadgeExposition />
          <Lessons setActive={setActive} />
          <ReviewListRedesing setActive={setActive} reviewCount={reviewCount} setReviewCount={setReviewCount} />
        </div>
      </NewContainer>
      <ProjectCourse setActive={setActive} />
      <ProgresiveLearning reviewCount={reviewCount} />
      <Faq />
      <ComunityReviews />
      {!data?.is_free && (
        <>
          <PricingSection />
          <StaticWarranty />
        </>
      )}
      {/* <MobileBanner /> */}
      <Footer />
    </>
  )
}

Course.pageTheme = {
  background: 'var(--sky)',
}


export async function getStaticPaths() {
  const courses = await getCourseListService()
  const paths = []
  courses.forEach(({ slug }) => paths.push({ params: { course: slug } }))
  return {
    paths,
    fallback: true,
  }
}

export async function getStaticProps({ params: { course } }) {
  const queryClient = new QueryClient()
  const data = await getCourseService({ course_slug: course })
  const queryClientCourse = await prefetchCourse({ slug: course, client: queryClient })
  await prefetchReviews({ queryClient, course })
  await prefetchBadge({ queryClient, course })
  await prefetchCourseLessons({ queryClient, course })
  await prefetchSubscriptionPlans({ queryClient, product: 'ALL' })
  return {
    props: {
      dehydratedState: dehydrate(queryClientCourse),
      courseSlug: course,
      openGraph: {
        title: data?.title,
        description: flatEditorJsContent(data?.description),
        url: `https://leonidasesteban.com/cursos/${course}`,
        image: data?.featured_video?.thumbnails[2]?.link,
        keyword: data?.meta,
      },
    },
    revalidate: 60 * 10,
  }
}

export default Course
